import React, { useMemo, useState } from 'react';
import { useAppContext } from '../context/AppContext';
import { db } from '../utils/db';
import { getCurrentMonthSpending } from '../utils/guiltFree';
import { getTopLeak, getLeakBucketSpending, formatINR } from '../utils/leakBuckets';
import { generateInsights } from '../utils/insights';
import { detectRecurring } from '../utils/categorization';

function parseAmt(str) {
    if (typeof str === 'number') return Math.abs(str);
    if (!str) return 0;
    return Math.abs(parseFloat(String(str).replace(/[^0-9.-]/g, '')) || 0);
}

/**
 * Reality Check — make the user feel deeply understood before asking for numbers.
 */
export default function RealityCheckView({ onContinue, onShare }) {
    const { allTransactions, isSampleMode, sampleIncome, triggerSampleLocked } = useAppContext();

    const stats = useMemo(() => {
        const debits = allTransactions.filter(t => t.type && String(t.type).toUpperCase() === 'DEBIT');
        const totalSpent = debits.reduce((s, t) => s + parseAmt(t.amount), 0);
        const top = getTopLeak(debits);
        const insights = generateInsights(debits).slice(0, 3);
        const recurring = detectRecurring(debits);
        const knownSubs = recurring.filter(r => r.isKnownSubscription);
        const subLock = knownSubs.reduce((s, r) => s + (r.avgAmount || 0), 0);
        const month = getCurrentMonthSpending(debits);
        const buckets = getLeakBucketSpending(debits);
        const topCats = Object.entries(month.byCategory || {})
            .sort((a, b) => b[1] - a[1])
            .slice(0, 4);
        return {
            totalSpent,
            tapCount: debits.length,
            topLeakLabel: top && top.spent > 0 ? top.bucket.label : null,
            topLeakSpend: top?.spent || 0,
            insights,
            knownSubs: knownSubs.slice(0, 5),
            subLock,
            topCats,
            buckets,
            estimatedGuess: Math.round(totalSpent * 0.42),
        };
    }, [allTransactions]);

    const [income, setIncome] = useState(() => {
        if (isSampleMode && sampleIncome) return String(sampleIncome);
        return localStorage.getItem('spenderly_monthly_income') || '';
    });
    const [fixed, setFixed] = useState(() => {
        const stored = localStorage.getItem('spenderly_monthly_fixed');
        if (stored) return stored;
        // Pre-fill with detected subs + suggest rent empty
        return stats.subLock > 0 ? String(Math.round(stats.subLock)) : '';
    });
    const [savings, setSavings] = useState(() => localStorage.getItem('spenderly_target_savings') || '');

    const daily = useMemo(() => {
        const inc = parseFloat(income) || 0;
        const fix = parseFloat(fixed) || 0;
        const sav = parseFloat(savings) || 0;
        if (inc <= 0) return null;
        const safe = Math.max(0, inc - fix - sav);
        return Math.floor(safe / 30);
    }, [income, fixed, savings]);

    const handleContinue = async () => {
        if (isSampleMode) {
            triggerSampleLocked();
            return;
        }
        const inc = parseFloat(income) || 0;
        const fix = parseFloat(fixed) || 0;
        const sav = parseFloat(savings) || 0;
        localStorage.setItem('spenderly_monthly_income', String(inc));
        localStorage.setItem('spenderly_monthly_fixed', String(fix));
        localStorage.setItem('spenderly_target_savings', String(sav));
        localStorage.setItem('spenderly_reality_done', '1');

        // Persist detected subscriptions as fixed expenses (local, free)
        const existing = await db.fixed_expenses.toArray();
        const names = new Set(existing.map(e => (e.name || '').toLowerCase()));
        for (const r of stats.knownSubs) {
            if (names.has(r.merchant.toLowerCase())) continue;
            await db.fixed_expenses.add({
                name: r.merchant,
                amount: Math.round(r.avgAmount),
                type: 'subscription',
                frequency: 'monthly',
                lastPaidDate: r.lastDate || new Date().toISOString().split('T')[0],
                isAutoDetected: true,
            });
            names.add(r.merchant.toLowerCase());
        }

        // Residual after detected subs → rent / EMI / other
        const residual = Math.max(0, Math.round(fix - stats.subLock));
        if (residual > 0) {
            const rent = existing.find(e => e.name === 'Rent / EMI / other fixed');
            if (rent?.id != null) {
                await db.fixed_expenses.update(rent.id, { amount: residual });
            } else if (![...names].includes('rent / emi / other fixed')) {
                await db.fixed_expenses.add({
                    name: 'Rent / EMI / other fixed',
                    amount: residual,
                    type: 'rent',
                    frequency: 'monthly',
                    lastPaidDate: new Date().toISOString().split('T')[0],
                    isAutoDetected: false,
                });
            }
        }

        if (sav > 0) {
            const goals = await db.goals.toArray();
            if (goals.length === 0) {
                const deadline = new Date();
                deadline.setMonth(deadline.getMonth() + 3);
                await db.goals.add({
                    name: 'Monthly savings target',
                    targetAmount: sav * 3,
                    savedSoFar: 0,
                    deadline: deadline.toISOString().split('T')[0],
                });
            }
        }

        onContinue?.();
    };

    return (
        <div className="leak-flow-view reality-rich">
            <p className="leak-eyebrow">Reality check</p>
            <h1 className="leak-display">
                You spent <span className="leak-accent">{formatINR(stats.totalSpent)}</span>
            </h1>
            <p className="leak-sub">
                {stats.tapCount} taps · most people guess ~{formatINR(stats.estimatedGuess)}
            </p>

            {stats.topLeakLabel && (
                <div className="reality-callout">
                    <span className="reality-callout-label">Biggest leak</span>
                    <p>
                        <strong>{stats.topLeakLabel}</strong> · {formatINR(stats.topLeakSpend)}
                        {stats.totalSpent > 0
                            ? ` (${Math.round((stats.topLeakSpend / stats.totalSpent) * 100)}%)`
                            : ''}
                    </p>
                </div>
            )}

            {stats.knownSubs.length > 0 && (
                <div className="reality-block">
                    <h3>Recurring · {formatINR(stats.subLock)}/mo</h3>
                    <ul className="home-recurring-list">
                        {stats.knownSubs.map(r => (
                            <li key={r.merchant}>
                                <span>{r.merchant}</span>
                                <strong>{formatINR(r.avgAmount)}</strong>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {stats.insights.length > 0 && (
                <div className="reality-block">
                    <h3>Noticed</h3>
                    <ul className="home-insight-list">
                        {stats.insights.map(ins => (
                            <li key={ins.id}>
                                <span className="home-insight-kicker">{ins.title}</span>
                                <p>{ins.body}</p>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            {stats.topCats.length > 0 && (
                <div className="reality-block">
                    <h3>Top categories</h3>
                    <ul className="home-cat-list">
                        {stats.topCats.map(([cat, amt]) => (
                            <li key={cat}>
                                <div className="home-cat-top">
                                    <span>{cat}</span>
                                    <strong>{formatINR(amt)}</strong>
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>
            )}

            <div className="leak-setup">
                <p className="leak-setup-label">Safe-to-spend inputs</p>
                <label className="leak-field">
                    <span>Monthly income</span>
                    <input
                        className="sp-input"
                        type="number"
                        inputMode="numeric"
                        placeholder="60000"
                        value={income}
                        onChange={e => setIncome(e.target.value)}
                    />
                </label>
                <label className="leak-field">
                    <span>Fixed outflows</span>
                    <input
                        className="sp-input"
                        type="number"
                        inputMode="numeric"
                        placeholder="25000"
                        value={fixed}
                        onChange={e => setFixed(e.target.value)}
                    />
                </label>
                <label className="leak-field">
                    <span>Savings target / mo</span>
                    <input
                        className="sp-input"
                        type="number"
                        inputMode="numeric"
                        placeholder="15000"
                        value={savings}
                        onChange={e => setSavings(e.target.value)}
                    />
                </label>

                {daily !== null && (
                    <div className="leak-daily">
                        <span className="leak-daily-num">{formatINR(daily)}</span>
                        <span className="leak-daily-unit"> / day</span>
                    </div>
                )}
            </div>

            <div className="leak-actions">
                <button
                    type="button"
                    className="sp-btn-primary"
                    disabled={!income || parseFloat(income) <= 0}
                    onClick={handleContinue}
                >
                    Set leak caps →
                </button>
                {onShare && (
                    <button type="button" className="sp-btn-ghost" onClick={onShare}>
                        Share
                    </button>
                )}
            </div>
        </div>
    );
}
